import { Music } from './audio/music';
import { AudioEngine } from './audio/synth';
import { Session } from './game/session';
import { Input } from './input/input';
import { ApiError, RunPool, api, flushPending, isNetworkError, type RunTicket } from './net/api';
import { Fx } from './render/fx';
import { Renderer } from './render/renderer';
import { GROUND, HEAD_H, STEP_MS, TICK_HZ } from './sim/constants';
import { LEVELS, SCENE_NAMES, SCENE_ORDER, levelById, nextLevelId } from './sim/levels';
import { dailySeed, randomSeed, utcDay } from './sim/rng';
import type { Mode, RankedMode, RunSummary, SceneId, SimConfig } from './sim/types';
import { store, type Settings } from './store';
import { renderAdventure } from './ui/adventure';
import { BoardScreen, renderBoard } from './ui/board';
import { Calibrator, offsetLabel } from './ui/calibrate';
import { $, el, fmt, formatDuration, starsEl, statsList } from './ui/dom';

// ---------- setup ----------

const app = $('app');
const canvas = $<HTMLCanvasElement>('game');
const renderer = new Renderer(canvas);
const fx = new Fx();
const audio = new AudioEngine();
const music = new Music(audio);
const runs = new RunPool();
const board = new BoardScreen();
let settings: Settings = store.settings();

type ScreenId = 'title' | 'pause' | 'over' | 'board' | 'adventure' | 'level' | 'practice' | 'settings' | 'calibrate';
const SCREENS: Record<ScreenId, HTMLElement> = {
  title: $('scrTitle'),
  pause: $('scrPause'),
  over: $('scrOver'),
  board: $('scrBoard'),
  adventure: $('scrAdventure'),
  level: $('scrLevel'),
  practice: $('scrPractice'),
  settings: $('scrSettings'),
  calibrate: $('scrCalibrate'),
};
let screen: ScreenId | null = 'title';
const history: ScreenId[] = [];

const hud = $('hud');
const hudScore = $('hudScore');
const hudMult = $('hudMult');
const hudCombo = $('hudCombo');
const hudBest = $('hudBest');
const hudMode = $('hudMode');
const hudPower = $('hudPower');
const btnMute = $<HTMLButtonElement>('btnMute');

// ---------- game state ----------

type StartCfg = Omit<SimConfig, 'seed'>;
let session: Session | null = null;
let lastCfg: StartCfg = { mode: 'arcade' };
let playing = false;
let overHandled = false;
let starting = false;
let demoSceneIdx = 0;
let demo = makeDemo(SCENE_ORDER[0]);
let demoSince = performance.now();

function makeDemo(scene: SceneId): Session {
  return new Session({ mode: 'practice', seed: randomSeed(), practicePeriod: 1.25, scene }, null, performance.now(), true);
}

const isRanked = (m: Mode): m is RankedMode => m === 'arcade' || m === 'daily';

// ---------- small helpers ----------

let toastTimer = 0;
function toast(text: string, ms = 1800): void {
  const t = $('toast');
  t.textContent = text;
  t.hidden = false;
  clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => (t.hidden = true), ms);
}

function vibrate(pattern: number | number[]): void {
  if (!settings.vibration) return;
  try {
    navigator.vibrate?.(pattern);
  } catch {
    // not supported
  }
}

function show(next: ScreenId | null, remember = false): void {
  if (remember && screen && screen !== next) history.push(screen);
  screen = next;
  for (const [id, node] of Object.entries(SCREENS)) node.hidden = id !== next;
  if (next && next !== 'title') {
    const first = SCREENS[next].querySelector<HTMLElement>('[data-autofocus], .btn.primary, button');
    first?.focus({ preventScroll: true });
  }
  if (next === 'title') resetIdle();
}

function back(): void {
  calibrator.cancel();
  const prev = history.pop();
  if (prev) show(prev);
  else show(session ? 'over' : 'title');
}

function applySettings(): void {
  audio.set({ sfx: settings.sfx, music: settings.music, volume: settings.volume });
  fx.reduced = settings.reducedMotion;
  document.documentElement.classList.toggle('reduced', settings.reducedMotion);
  const muted = !settings.sfx && !settings.music;
  btnMute.setAttribute('aria-pressed', String(muted));
  btnMute.setAttribute('aria-label', muted ? 'Activar sonido' : 'Silenciar');
}

function saveSettings(): void {
  store.saveSettings(settings);
  applySettings();
}

function toggleMute(): void {
  const muted = !settings.sfx && !settings.music;
  settings.sfx = muted;
  settings.music = muted;
  saveSettings();
  syncSettingsForm();
  toast(muted ? 'Sonido activado' : 'Sonido apagado', 1200);
}

// ---------- starting, pausing, ending ----------

async function startGame(cfg: StartCfg): Promise<void> {
  if (starting) return;
  starting = true;
  audio.unlock();
  try {
    let ticket: RunTicket | null = null;
    let seed = randomSeed();
    if (isRanked(cfg.mode)) {
      if (cfg.mode === 'daily') seed = dailySeed(utcDay(Date.now()));
      const slow = window.setTimeout(() => toast('Preparando partida…', 3000), 250);
      ticket = await runs.take(cfg.mode, 1500);
      clearTimeout(slow);
      if (ticket) {
        seed = ticket.seed;
        $('toast').hidden = true;
      } else {
        toast('Sin conexión: esta partida no entra en la tabla de campeones.', 2800);
      }
      void runs.prefetch(cfg.mode);
    }
    lastCfg = cfg;
    session = new Session({ ...cfg, seed }, ticket, performance.now() + 50);
    fx.reset();
    music.reset();
    renderer.setScene(session.sim.scene);
    audio.setAmbience(session.sim.scene);
    overHandled = false;
    playing = true;
    input.enabled = true;
    history.length = 0;
    show(null);
    hud.hidden = false;
    (document.activeElement as HTMLElement | null)?.blur?.();
    if (cfg.mode === 'adventure') {
      const l = levelById(cfg.levelId)!;
      toast(`Nivel ${l.id}: ${l.name}. Meta: ${l.goalJumps} saltos`, 2400);
    } else if (cfg.mode === 'daily') {
      toast('Reto del día: la misma cuerda para todos', 2200);
    }
  } finally {
    starting = false;
  }
}

function pauseGame(): void {
  if (!session || !playing || session.paused || session.sim.over) return;
  const now = performance.now();
  session.push(now, 0);
  session.advance(now);
  session.pause(now);
  input.releaseAll(now);
  playing = false;
  input.enabled = false;
  show('pause');
}

function resumeGame(): void {
  if (!session) return;
  show(null);
  const cd = $('countdown');
  let n = 3;
  cd.hidden = false;
  cd.textContent = String(n);
  const tick = () => {
    n--;
    if (n > 0) {
      cd.textContent = String(n);
      window.setTimeout(tick, 420);
      return;
    }
    cd.hidden = true;
    if (!session || screen !== null) return;
    session.resume(performance.now());
    playing = true;
    input.enabled = true;
  };
  window.setTimeout(tick, 420);
}

function toMenu(): void {
  session = null;
  playing = false;
  input.enabled = false;
  hud.hidden = true;
  history.length = 0;
  fx.reset();
  demo = makeDemo(renderer.currentScene);
  demoSince = performance.now();
  audio.setAmbience(null);
  show('title');
}

function onGameOver(s: Session): void {
  playing = false;
  input.enabled = false;
  const sum = s.sim.summary();
  const mode = s.sim.mode;
  if (mode === 'adventure') {
    const id = s.sim.level!.id;
    if (sum.won) store.setStars(id, sum.stars);
    window.setTimeout(() => showLevelEnd(id, sum), sum.won ? 700 : 950);
    return;
  }
  const prevBest = store.best(mode);
  const isBest = store.setBest(mode, sum.score) && sum.score > 0;
  window.setTimeout(() => showOver(s, sum, isBest, prevBest), 950);
}

// ---------- game over (arcade, daily) ----------

const nickForm = $<HTMLFormElement>('nickForm');
const nickInput = $<HTMLInputElement>('nickInput');
const nickMsg = $('nickMsg');
const nickSave = $<HTMLButtonElement>('nickSave');
const btnAgain = $<HTMLButtonElement>('btnAgain');

function showOver(s: Session, sum: RunSummary, isBest: boolean, prevBest: number): void {
  if (session !== s) return;
  const mode = s.sim.mode as RankedMode;
  $('overTitle').textContent = isBest ? '¡Nuevo récord!' : '¡Se enredó!';
  $('overScore').textContent = fmt(sum.score);
  statsList($('overStats'), [
    ['Saltos', fmt(sum.jumps)],
    ['Perfectos', fmt(sum.perfects)],
    ['Mejor combo', fmt(sum.bestCombo)],
    ['Moscas', fmt(sum.flies)],
    ['Tiempo', formatDuration((sum.ticks / TICK_HZ) * 1000)],
    ['Tu récord', fmt(Math.max(prevBest, sum.score))],
  ]);
  const note = $('overNote');
  note.textContent = '';
  nickForm.hidden = true;
  nickMsg.textContent = '';
  nickMsg.className = 'form-msg';
  nickInput.disabled = false;
  nickSave.disabled = false;
  nickSave.hidden = false;
  if (!s.ticket) {
    note.textContent = 'Esta partida se jugó sin conexión, así que no entra en la tabla de campeones.';
  } else if (sum.score <= 0) {
    note.textContent = 'Salta al menos una vez para entrar en la tabla de campeones.';
  } else {
    nickForm.hidden = false;
    nickInput.value = store.nickname();
    api
      .leaderboard(mode, mode === 'daily' ? 'day' : 'all', undefined, 1)
      .then((b) => {
        if (session !== s || b.cutoff === null || sum.score > b.cutoff) return;
        nickForm.hidden = true;
        note.textContent = `Te faltaron ${fmt(b.cutoff - sum.score + 1)} puntos para entrar en el top 100.`;
      })
      .catch(() => {});
  }
  show('over');
  // Avoid restarting by accident with the same key press that tangled the frog.
  btnAgain.disabled = true;
  window.setTimeout(() => {
    btnAgain.disabled = false;
    if (screen === 'over' && document.activeElement !== nickInput) btnAgain.focus({ preventScroll: true });
  }, 700);
}

nickForm.addEventListener('submit', async (e) => {
  e.preventDefault();
  const s = session;
  if (!s || !s.ticket) return;
  const nickname = nickInput.value.normalize('NFKC').replace(/\s+/g, ' ').trim();
  const len = [...nickname].length;
  if (len < 3 || len > 12) {
    nickMsg.className = 'form-msg err';
    nickMsg.textContent = 'El apodo debe tener entre 3 y 12 caracteres.';
    nickInput.focus();
    return;
  }
  nickSave.disabled = true;
  nickInput.disabled = true;
  nickMsg.className = 'form-msg';
  nickMsg.textContent = 'Guardando…';
  const t = s.ticket;
  const body = { runId: t.runId, token: t.token, nickname, inputs: s.sim.log, score: s.sim.score };
  try {
    const r = await api.submitScore(body);
    store.setNickname(r.nickname);
    store.setMyScore(t.mode, r.scoreId, r.score, utcDay(Date.now()));
    nickMsg.className = 'form-msg ok';
    nickMsg.textContent = t.mode === 'daily'
      ? `¡Guardado! Puesto ${r.rank} de ${fmt(r.total)} en el reto de hoy.`
      : `¡Guardado! Puesto ${r.rank} de ${fmt(r.total)} en el histórico y ${r.rankDay} de hoy.`;
    nickSave.hidden = true;
  } catch (err) {
    if (isNetworkError(err)) {
      store.setPending([...store.pending(), { ...body, mode: t.mode, savedAt: Date.now() }]);
      nickMsg.className = 'form-msg';
      nickMsg.textContent = 'Sin conexión. Guardamos tu récord y lo enviaremos cuando vuelva la conexión.';
      nickSave.hidden = true;
      return;
    }
    const status = err instanceof ApiError ? err.status : 0;
    nickMsg.className = 'form-msg err';
    nickMsg.textContent = err instanceof Error ? err.message : 'No se pudo guardar.';
    if (status === 400) {
      // bad nickname: let them fix it
      nickSave.disabled = false;
      nickInput.disabled = false;
      nickInput.focus();
    } else {
      nickSave.hidden = true;
    }
  }
});

// ---------- adventure ----------

function openAdventure(remember = true): void {
  renderAdventure((id) => void startGame({ mode: 'adventure', levelId: id }));
  show('adventure', remember);
}

function showLevelEnd(id: string, sum: RunSummary): void {
  const l = levelById(id)!;
  const won = sum.won;
  $('lvlId').textContent = `Nivel ${l.id} · ${l.name}`;
  $('lvlTitle').textContent = won ? (l.boss ? '¡Jefe vencido!' : '¡Nivel superado!') : '¡Se enredó!';
  const starsBox = $('lvlStars');
  starsBox.replaceChildren(...starsEl(won ? sum.stars : 0, 3, 'big').childNodes);
  starsBox.setAttribute('aria-label', `${won ? sum.stars : 0} de 3 estrellas`);
  statsList($('lvlStats'), [
    ['Saltos', `${sum.jumps} / ${l.goalJumps}`],
    ['Perfectos', `${sum.jumps ? Math.round((sum.perfects / sum.jumps) * 100) : 0} %`],
    ['Moscas', `${sum.flies} / ${l.fliesFor3}`],
    ['Puntos', fmt(sum.score)],
  ]);
  let note = '';
  if (!won) note = `Te faltaron ${l.goalJumps - sum.jumps} saltos. ¡Otra vez!`;
  else if (sum.stars === 1) note = 'Para la 2.ª estrella: consigue Perfecto en la mitad de los saltos.';
  else if (sum.stars === 2) note = `Para la 3.ª estrella: además atrapa ${l.fliesFor3} moscas con la lengua.`;
  else note = '¡Nivel completo con 3 estrellas!';
  $('lvlNote').textContent = note;
  const next = nextLevelId(id);
  const btnNext = $<HTMLButtonElement>('btnNext');
  btnNext.hidden = !won || !next;
  show('level');
}

// ---------- practice ----------

const practiceSpeed = $<HTMLInputElement>('practiceSpeed');
let practiceScene: SceneId = (store.practice().scene as SceneId) ?? 'pond';

function syncPractice(): void {
  const v = Number(practiceSpeed.value);
  $('practiceSpeedOut').textContent = `${v.toFixed(2).replace('.', ',')} s por vuelta`;
  $('practiceScenes').querySelectorAll<HTMLButtonElement>('button').forEach((b) =>
    b.setAttribute('aria-pressed', String(b.dataset.scene === practiceScene)),
  );
}

function openPractice(): void {
  const p = store.practice();
  practiceSpeed.value = String(p.period);
  practiceScene = SCENE_ORDER.includes(p.scene as SceneId) ? (p.scene as SceneId) : 'pond';
  const seg = $('practiceScenes');
  if (!seg.childElementCount) {
    for (const id of SCENE_ORDER) {
      const b = el('button', { text: SCENE_NAMES[id], attrs: { type: 'button', 'data-scene': id } });
      b.addEventListener('click', () => {
        practiceScene = id;
        syncPractice();
        renderer.setScene(id);
      });
      seg.append(b);
    }
  }
  syncPractice();
  show('practice', true);
}
practiceSpeed.addEventListener('input', syncPractice);

// ---------- settings & calibration ----------

const setIds = {
  music: $<HTMLInputElement>('setMusic'),
  sfx: $<HTMLInputElement>('setSfx'),
  volume: $<HTMLInputElement>('setVolume'),
  vibration: $<HTMLInputElement>('setVibration'),
  rhythmRing: $<HTMLInputElement>('setRing'),
  highContrast: $<HTMLInputElement>('setContrast'),
  reducedMotion: $<HTMLInputElement>('setReduced'),
};

function syncSettingsForm(): void {
  setIds.music.checked = settings.music;
  setIds.sfx.checked = settings.sfx;
  setIds.volume.value = String(settings.volume);
  setIds.vibration.checked = settings.vibration;
  setIds.rhythmRing.checked = settings.rhythmRing;
  setIds.highContrast.checked = settings.highContrast;
  setIds.reducedMotion.checked = settings.reducedMotion;
  $('offsetText').textContent = offsetLabel(settings.offsetMs);
}

for (const [key, input] of Object.entries(setIds) as [keyof typeof setIds, HTMLInputElement][]) {
  input.addEventListener(key === 'volume' ? 'input' : 'change', () => {
    if (key === 'volume') settings.volume = Number(input.value);
    else settings[key] = input.checked;
    saveSettings();
  });
}

const calPad = $<HTMLButtonElement>('calPad');
const calibrator = new Calibrator(
  audio,
  (text) => ($('calStatus').textContent = text),
  (offset) => {
    $<HTMLButtonElement>('calStartBtn').disabled = false;
    if (offset === null) return;
    settings.offsetMs = offset;
    saveSettings();
    syncSettingsForm();
    $('calStatus').textContent = offsetLabel(offset) + ' Guardado.';
  },
);
calPad.addEventListener('pointerdown', (e) => {
  e.preventDefault();
  calibrator.tap(e.timeStamp);
  calPad.classList.add('hit');
  window.setTimeout(() => calPad.classList.remove('hit'), 90);
});
window.addEventListener('keydown', (e) => {
  if (screen === 'calibrate' && e.code === 'Space' && calibrator.active) {
    e.preventDefault();
    calibrator.tap(e.timeStamp);
  }
});

// ---------- actions ----------

const actions: Record<string, () => void> = {
  arcade: () => void startGame({ mode: 'arcade' }),
  daily: () => void startGame({ mode: 'daily' }),
  adventure: () => openAdventure(),
  practice: () => openPractice(),
  board: () => {
    board.open(session && isRanked(session.sim.mode) ? session.sim.mode : undefined);
    show('board', true);
  },
  settings: () => {
    syncSettingsForm();
    $('resetConfirm').hidden = true;
    show('settings', true);
  },
  back,
  menu: toMenu,
  resume: resumeGame,
  restart: () => void startGame(lastCfg),
  again: () => void startGame(lastCfg),
  retry: () => void startGame(lastCfg),
  next: () => {
    const next = lastCfg.levelId ? nextLevelId(lastCfg.levelId) : undefined;
    if (next) void startGame({ mode: 'adventure', levelId: next });
  },
  map: () => {
    toMenu();
    openAdventure(true);
  },
  startPractice: () => {
    const period = Number(practiceSpeed.value);
    store.setPractice({ period, scene: practiceScene });
    void startGame({ mode: 'practice', practicePeriod: period, scene: practiceScene });
  },
  calibrate: () => {
    $('calStatus').textContent = 'Pulsa Empezar cuando estés listo.';
    show('calibrate', true);
  },
  calStart: () => {
    $<HTMLButtonElement>('calStartBtn').disabled = true;
    calibrator.start();
    calPad.focus({ preventScroll: true });
  },
  calReset: () => {
    settings.offsetMs = 0;
    saveSettings();
    syncSettingsForm();
    $('calStatus').textContent = 'Ajuste quitado.';
  },
  askReset: () => ($('resetConfirm').hidden = false),
  cancelReset: () => ($('resetConfirm').hidden = true),
  doReset: () => {
    store.clearProgress();
    $('resetConfirm').hidden = true;
    toast('Progreso borrado', 1500);
  },
};

document.addEventListener('click', (e) => {
  const target = (e.target as Element).closest<HTMLElement>('[data-action]');
  if (!target || (target as HTMLButtonElement).disabled) return;
  audio.unlock();
  audio.click();
  actions[target.dataset.action!]?.();
});

$('btnPause').addEventListener('click', pauseGame);
btnMute.addEventListener('click', toggleMute);

// ---------- input ----------

const input = new Input(app, {
  down(ts) {
    audio.unlock();
    session?.push(ts, 1);
  },
  up(ts) {
    session?.push(ts, 0);
  },
  pause: pauseGame,
  mute: toggleMute,
});

const isUi = (t: EventTarget | null) => t instanceof Element && !!t.closest('button, input, a, label, .panel');

// Title screen: tap the pond or press Space/Enter to play Arcade, like a cabinet.
app.addEventListener('pointerdown', (e) => {
  audio.unlock();
  if (screen === 'title') {
    resetIdle();
    if (!isUi(e.target)) {
      e.preventDefault();
      void startGame({ mode: 'arcade' });
    }
  }
});
window.addEventListener('keydown', (e) => {
  audio.unlock();
  if (screen !== 'title') {
    if (e.code === 'Escape' && screen && screen !== 'over' && screen !== 'level' && !session?.paused) back();
    else if (e.code === 'Escape' && screen === 'pause') resumeGame();
    return;
  }
  resetIdle();
  const onControl = e.target instanceof HTMLButtonElement || e.target instanceof HTMLInputElement;
  if ((e.code === 'Space' || e.code === 'Enter') && !onControl) {
    e.preventDefault();
    void startGame({ mode: 'arcade' });
  }
});
window.addEventListener('pointerup', () => audio.unlock());
window.addEventListener('touchend', () => audio.unlock());

function onPadMenu(): void {
  if (screen === 'title') void startGame({ mode: 'arcade' });
  else if (screen === 'pause') resumeGame();
  else if (screen === 'over' && !btnAgain.disabled) void startGame(lastCfg);
}

// Pause when the app is hidden or the phone is turned.
document.addEventListener('visibilitychange', () => {
  if (document.hidden) pauseGame();
});
try {
  matchMedia('(orientation: portrait)').addEventListener('change', () => pauseGame());
} catch {
  // old browsers
}
window.addEventListener('resize', () => renderer.resize());

// ---------- attract mode: title <-> leaderboard ----------

let idleSince = performance.now();
let attractBoardUntil = 0;
let attractCache: { at: number; ok: boolean } = { at: 0, ok: false };

function resetIdle(): void {
  idleSince = performance.now();
  if (attractBoardUntil) {
    attractBoardUntil = 0;
    $('titleBoard').hidden = true;
    $('titleMain').hidden = false;
  }
}

async function attractTick(): Promise<void> {
  if (screen !== 'title' || session) return;
  const now = performance.now();
  if (attractBoardUntil && now > attractBoardUntil) {
    resetIdle();
    return;
  }
  if (!attractBoardUntil && now - idleSince > 14000) {
    if (now - attractCache.at > 60000) {
      try {
        const b = await api.leaderboard('arcade', 'all', undefined, 8);
        renderBoard($('attractList'), b);
        attractCache = { at: now, ok: b.entries.length > 0 };
      } catch {
        attractCache = { at: now, ok: false };
      }
    }
    if (!attractCache.ok || screen !== 'title') {
      idleSince = now;
      return;
    }
    $('titleMain').hidden = true;
    $('titleBoard').hidden = false;
    attractBoardUntil = now + 9000;
  }
}
window.setInterval(() => void attractTick(), 1000);

// ---------- events from the simulation ----------

const GRADE_TEXT = { perfect: '¡Perfecto!', good: 'Bien', ok: 'Justo' } as const;
const GRADE_COLOR = { perfect: '#FFE28A', good: '#C8F59A', ok: '#FFFFFF' } as const;
const CATCH_TEXT = { fly: '+5', golden: '+25', slow: 'Hoja lenta', bubble: 'Burbuja', magnet: 'Imán' } as const;

function handleEvents(s: Session, live: boolean): void {
  const sim = s.sim;
  for (const e of sim.events) {
    switch (e.t) {
      case 'jump':
        fx.croakT = 0;
        if (live) audio.croak();
        break;
      case 'land':
        fx.squash = 1;
        fx.ripples.push(0);
        fx.burst(400, GROUND, 'rgba(220,245,255,0.9)', 6, 90, 0.4, 600, 2.5);
        if (sim.scene === 'ice') fx.slideV += (Math.random() < 0.5 ? -1 : 1) * 70;
        break;
      case 'cross': {
        const y = GROUND - HEAD_H - 40;
        fx.popup(GRADE_TEXT[e.grade], 400, y, GRADE_COLOR[e.grade], e.grade === 'perfect' ? 28 : 24);
        fx.popup(`+${e.points}`, 470, y + 34, '#FFFFFF', 20);
        if (e.grade === 'perfect') {
          fx.burst(400, GROUND - 20, '#FFE28A', 12, 170, 0.5, 300, 3);
          if (live) {
            audio.perfect(sim.combo);
            vibrate(12);
          }
          if (live && sim.combo > 0 && sim.combo % 4 === 0 && sim.combo <= 16) toast(`¡Multiplicador ×${1 + sim.combo / 4}!`, 1100);
        }
        if (live) {
          hudScore.classList.remove('bump');
          void hudScore.offsetWidth; // restart the CSS animation
          hudScore.classList.add('bump');
        }
        break;
      }
      case 'tangle':
        fx.tangleT = 0;
        fx.shake = e.fatal ? 1 : 0.5;
        if (live) {
          audio.tangle();
          vibrate([60, 40, 120]);
        }
        break;
      case 'bubble':
        fx.bubblePopT = 0;
        fx.burst(400, GROUND - 40, 'rgba(220,245,255,0.95)', 18, 220, 0.5, 200, 3);
        fx.popup('¡La burbuja te salvó!', 400, GROUND - 130, '#CFEFFF', 22);
        if (live) audio.bubblePop();
        break;
      case 'tongue':
        fx.tongue = { t: 0, x: e.x, h: e.h, hit: e.hit };
        if (live) audio.tongue();
        break;
      case 'catch':
        fx.popup(CATCH_TEXT[e.kind], e.x, GROUND - e.h - 18, e.kind === 'golden' ? '#FFD84D' : '#FFFFFF', 20);
        fx.burst(e.x, GROUND - e.h, e.kind === 'golden' ? '#FFD84D' : '#FFFFFF', 8, 120, 0.4, 200, 2.5);
        if (live) audio.catch(e.kind);
        break;
      case 'gust':
        if (sim.scene === 'storm') {
          fx.flash = 0.8;
          fx.flashColor = '#F4F8FF';
          fx.bolt = { x: 120 + Math.random() * 560, t: 0, seed: Math.floor(Math.random() * 1000) };
          if (live) {
            audio.thunder();
            toast(e.dir > 0 ? 'Ráfaga: la cuerda se acelera' : 'Ráfaga: la cuerda se frena', 1200);
          }
        }
        break;
      case 'scene':
        renderer.setScene(e.scene);
        if (live) {
          toast(SCENE_NAMES[e.scene], 2000);
          audio.setAmbience(e.scene);
        }
        break;
      case 'win':
        fx.burst(400, GROUND - 60, '#FFD84D', 30, 260, 0.9, 250, 3.5);
        fx.popup('¡Meta!', 400, GROUND - 150, '#FFD84D', 34);
        if (live) audio.catch('golden');
        break;
      case 'turn':
        break;
    }
  }
  sim.events.length = 0;
  // practice: the frog untangles after a moment
  if (fx.tangleT >= 0 && !sim.over && sim.stunUntil <= sim.tick) fx.tangleT = -1;
}

// ---------- HUD ----------

let hudKey = '';
function updateHud(s: Session): void {
  const sim = s.sim;
  const mult = 1 + Math.min(4, Math.floor(sim.combo / 4));
  const powers: string[] = [];
  if (sim.slowUntil > sim.tick) powers.push(`slow:${Math.ceil((sim.slowUntil - sim.tick) / TICK_HZ)}`);
  if (sim.magnetUntil > sim.tick) powers.push(`magnet:${Math.ceil((sim.magnetUntil - sim.tick) / TICK_HZ)}`);
  if (sim.bubble) powers.push('bubble:');
  const key = [sim.score, mult, sim.combo, sim.jumps, powers.join(',')].join('|');
  if (key === hudKey) return;
  hudKey = key;
  hudScore.textContent = fmt(sim.score);
  hudMult.textContent = `×${mult}`;
  hudMult.classList.toggle('hot', mult > 1);
  hudCombo.textContent = `Combo ${sim.combo}`;
  if (sim.mode === 'adventure') {
    hudMode.textContent = `Nivel ${sim.level!.id}`;
    hudBest.textContent = `Meta ${sim.jumps} / ${sim.level!.goalJumps}`;
  } else if (sim.mode === 'practice') {
    hudMode.textContent = 'Práctica';
    hudBest.textContent = `${sim.jumps} saltos`;
  } else {
    hudMode.textContent = sim.mode === 'daily' ? 'Reto del día' : 'Arcade';
    hudBest.textContent = `Récord ${fmt(Math.max(store.best(sim.mode), sim.score))}`;
  }
  hudPower.replaceChildren(
    ...powers.map((p) => {
      const [kind, secs] = p.split(':');
      const label = kind === 'slow' ? `Hoja lenta ${secs} s` : kind === 'magnet' ? `Imán ${secs} s` : 'Burbuja';
      return el('span', { class: `chip power-${kind}`, text: label });
    }),
  );
}

// ---------- main loop ----------

let last = performance.now();
let clock = 0;

function frame(now: number): void {
  const dt = Math.min(0.05, Math.max(0, (now - last) / 1000));
  last = now;
  clock += dt;
  input.pollGamepads(now, onPadMenu);

  if (!session && now - demoSince > 24000) {
    demoSceneIdx = (demoSceneIdx + 1) % SCENE_ORDER.length;
    demo = makeDemo(SCENE_ORDER[demoSceneIdx]);
    demoSince = now;
    fx.reset();
  }
  const s = session ?? demo;
  if (!session && renderer.currentScene !== s.sim.scene && screen !== 'practice') renderer.setScene(s.sim.scene);
  const alpha = s.advance(now);
  handleEvents(s, s === session);
  fx.update(dt);

  if (session && !session.paused && !session.sim.over) {
    music.update(session.sim, renderer.currentScene, settings.offsetMs / 1000);
  }
  renderer.draw(s.sim, fx, {
    reduced: settings.reducedMotion,
    highContrast: settings.highContrast,
    rhythmRing: settings.rhythmRing && s === session,
    leadTicks: s === session ? settings.offsetMs / STEP_MS : 0,
  }, clock, dt, alpha);

  if (session) {
    updateHud(session);
    if (session.sim.over && !overHandled) {
      overHandled = true;
      onGameOver(session);
    }
  }
  requestAnimationFrame(frame);
}

// ---------- boot ----------

applySettings();
syncSettingsForm();
show('title');
void runs.prefetch('arcade');
void flushPending();
window.addEventListener('online', () => void flushPending());
requestAnimationFrame(frame);

if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {});
  });
}

// Used by the end-to-end tests to check the game is running.
(window as unknown as { __rana: unknown }).__rana = {
  get state() {
    return { screen, playing, mode: session?.sim.mode ?? null, score: session?.sim.score ?? 0, over: session?.sim.over ?? false };
  },
  levels: LEVELS.length,
};
